// Single source of truth for AiMe's publicly-quoted plan pricing/features.
// Consumed by Homepage.tsx (marketing pricing section) and BillingPage.tsx
// (in-app upgrade cards) so the two can never drift out of sync again.
//
// NOTE: these are the advertised/display prices. The amount actually charged
// at checkout comes from Stripe (STRIPE_GROWTH_PRICE_ID / STRIPE_PRO_PRICE_ID /
// STRIPE_ENTERPRISE_PRICE_ID) — if Stripe's Price objects don't match these
// numbers, checkout will bill a different amount than the UI advertises.
// Verify Stripe prices match this file whenever it's edited.

export interface PricingPlan {
  id: 'growth' | 'pro' | 'enterprise';
  name: string;
  price: string;
  period: string;
  desc: string;
  trial: string | null;
  features: string[];
  featured: boolean;
}

export const PRICING_PLANS: PricingPlan[] = [
  {
    id: 'growth',
    name: 'Growth',
    price: '$249',
    period: '/mo',
    desc: 'Full platform. No limits.',
    trial: '14-day free trial included',
    features: [
      'AI content, campaigns & site builder',
      'Multi-variant campaign generation (up to 5 angles)',
      'Lead capture, CRM & scheduling',
      'Analytics, SEO & multi-channel',
      'Digital marketplace & video delivery',
      'Scripts, quizzes & landing pages',
    ],
    featured: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$499',
    period: '/mo',
    desc: 'Growth plus power features',
    trial: '14-day free trial included',
    features: [
      'Everything in Growth',
      'Email A/B testing with auto-promotion',
      'Domain registration & DNS management',
      'Recurring digital product subscriptions',
      'Advanced product & revenue analytics',
    ],
    featured: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: '$2499',
    period: '/mo',
    desc: 'Built for teams at scale',
    trial: null,
    features: [
      'Everything in Pro',
      'Team management & role-based access',
      'Autonomous test-and-scale loop',
      'Ad platform integrations (Meta & Google)',
      'Priority support & dedicated SLA',
    ],
    featured: false,
  },
];

// Trial users land on Growth after their 14-day trial ends (see the homepage
// FAQ: "The trial gives you full access to the Growth tier").
export const TRIAL_CONVERSION_PLAN = PRICING_PLANS.find(p => p.id === 'growth')!;
